import { MemberChipPiecesReview } from './MemberChipPiecesReview';
import { moneyCents } from './timedFeeDefaults';
import TimedEventExpenses from './TimedEventExpenses';
import { useLocalBusinessConsent } from '../../hooks/useLocalBusinessConsent';
import { formatAccountingInstant } from '../../lib/accounting-time';
import { useAccountingDisplayTimezone } from '../../hooks/useAccountingDisplayTimezone';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';
import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { TimedSettlementBudgetSummary } from './TimedSettlementBudgetSummary';
import './TimedSettlementReceipt.css';
import type {
  TimedSettlementReadMetadata,
  TimedSettlementMethod,
  TimedRankEligibility,
  TimedCustodyPlanV1,
  SettlementQualificationStatus,
  TimedSettlementFinancialTotals,
  TimedEventExpenseSummary,
  MemberDto,
  PaginatedMembersDto,
} from '@pokerroom/types';
import { api } from '../../lib/api.ts';
import { ActionKey } from '@pokerroom/types';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  CpCheckPanel,
  SettlementSteps,
  SettlementSummary,
  cpCheckSummary,
  settlementStep,
} from './timedSettlementView';

interface ParticipantInput {
  settlementMethod?: TimedSettlementMethod;
  collectedVenueFee?: string;
  memberNo: string;
  raCount: number;
  activityParticipant: boolean;
}

interface DraftInput {
  settlementContractVersion?: 2;
  paperReference: string;
  rules: {
    startingChips: number;
    raChips: number;
    prizeContribution: number;
    activityFee1: number;
    zCustomerLevelCode: string;
  };
  participants: ParticipantInput[];
}

interface ParticipantResult {
  chipCounts?: Record<string, number> | null;
  countRevisionId?: string | null;
  settlementMethod?: TimedSettlementMethod;
  collectedVenueFee?: string;
  entryDue?: string;
  memberId?: string | null;
  memberNo: string;
  nickname?: string;
  displayName?: string;
  memberLevel?: { code: string; displayName: string };
  seat: string | null;
  tableNo?: number | null;
  seatNo?: number | null;
  activityParticipant?: boolean;
  rank: number;
  rankEligibility?: TimedRankEligibility;
  purchasedChips: number;
  finalChips: number;
  netChips: number;
  baseAward: number;
  rankBonus: number;
  totalAward: number;
  zPoints?: number;
  prize?: number | null;
  participationPoints?: number | null;
  rankPoints?: number | null;
  zipaCredits?: number | null;
  note?: string;
}

interface Preview {
  calculationRevision?: number;
  tournamentName?: string;
  rules?: { settlementLevel?: { name: string } | null };
  totals: TimedSettlementFinancialTotals & {
    participants: number;
    raCount: number;
    units: number;
    expectedChips: number;
    countedChips: number;
    chipDifference: number;
    missingCounts?: number;
    expectedPieces?: Record<string, number> | null;
    countedPieces?: Record<string, number> | null;
    denominationMismatch?: boolean;
  };
  participants: ParticipantResult[];
  denominationException?: { reason: string; actorId: string | null; acceptedAt: string };
}

interface EventPreparation {
  id: string;
  cycle: number;
  sourceHash: string;
  createdAt: string;
  source: {
    custody?: TimedCustodyPlanV1;
    draftVersion: number;
    payload: DraftInput;
    preview: Preview;
    members: Array<{
      memberId: string;
      memberNo: string;
      method: 'C' | 'P';
      unpaid: string;
      award: string;
      completedId: string | null;
    }>;
  };
}
interface EventWorkflow {
  canRecover: boolean;
  cycle: number;
  preparation: EventPreparation | null;
  finish: { status: string; id: string } | null;
}
interface RecoveryPreview {
  custody?: {
    kind: 'ENTRY' | 'AWARD';
    amount: string;
    fromLocationId: string;
    toLocationId: string;
  }[];
  cycle: number;
  sourceHash: string;
  businessDate: string;
  members: Array<{
    id: string;
    memberId: string;
    displayName: string;
    memberNo: string;
    method: string;
    entryRefund: string;
    awardReturn: string;
  }>;
}

interface SettlementResponse extends TimedSettlementReadMetadata {
  correctionPending?: boolean;
  qualification?: SettlementQualificationStatus;
  tournamentName: string;
  phase: 'PREPARATION' | 'COUNTING' | 'CLOSED';
  gameCloseAvailable?: boolean;
  closure?: { closed: boolean; completedAt: string; sourceValid: boolean } | null;
  identities: Record<
    string,
    { memberId?: string; displayName: string; phoneSuffix: string | null }
  >;
  levels: Array<{ code: string; displayName: string; isZCustomer?: boolean }>;
  settlementLevels: Array<{
    id: string;
    name: string;
    startingChips: number;
    initialCounts: Record<string, number>;
    raCounts: Record<string, number>;
    raChips: number;
    activityFee1: number;
    prizeContribution: number;
    participationPoints: number;
    rankPoints: number[];
  }>;
  settlementLevel: {
    id: string;
    name: string;
    startingChips: number;
    raChips: number;
    activityFee1: number;
    prizeContribution: number;
    initialCounts: Record<string, number>;
    raCounts: Record<string, number>;
    denominations: number[];
    zCustomerLevelCode?: string;
    sourceLevel?: { name: string };
  } | null;
  settlementLevelVersion: number;
  draft: (DraftInput & { version: number }) | null;
  preview: Preview | null;
  previewStale?: boolean;
  confirmed: {
    version: number;
    confirmedAt: string;
    preview: Preview;
    payload: DraftInput;
  } | null;
}

export const emptyParticipant = (legacy = false): ParticipantInput => ({
  ...(!legacy ? { settlementMethod: 'CASH' as const, collectedVenueFee: '0' } : {}),
  memberNo: '',
  raCount: 0,
  activityParticipant: true,
});

const emptyDraft: DraftInput = {
  settlementContractVersion: 2,
  paperReference: '',
  rules: {
    startingChips: 0,
    raChips: 0,
    prizeContribution: 0,
    activityFee1: 0,
    zCustomerLevelCode: '',
  },
  participants: [],
};

export function defaultZCustomerLevelCode(
  currentCode: string,
  snapshotCode: string | undefined,
  levels: Array<{ code: string; isZCustomer?: boolean }>,
): string {
  return currentCode || snapshotCode || levels.find((level) => level.isZCustomer)?.code || '';
}

export function savedDraftRefreshInput(draft: DraftInput & { version: number }) {
  return {
    ...(draft.settlementContractVersion === 2 ? { settlementContractVersion: 2 } : {}),
    paperReference: draft.paperReference,
    rules: draft.rules,
    participants: draft.participants,
    expectedVersion: draft.version,
  };
}

export function autoRefreshKey({
  id,
  draftVersion,
  previewStale,
  dirty,
  overrideDirty,
  receiptVersion,
  attemptedKey,
  confirmed = false,
}: {
  id: string | undefined;
  draftVersion: number | undefined;
  previewStale: boolean | undefined;
  dirty: boolean;
  overrideDirty: boolean;
  receiptVersion: string;
  attemptedKey: string | null;
  confirmed?: boolean;
}) {
  if (
    !id ||
    draftVersion === undefined ||
    !previewStale ||
    dirty ||
    overrideDirty ||
    receiptVersion ||
    confirmed
  )
    return null;
  const key = `${id}:${draftVersion}`;
  return key === attemptedKey ? null : key;
}

function errorMessage(error: unknown): string | null {
  if (isAxiosError(error)) {
    const validation = error.response?.data?.errors?.validation;
    if (Array.isArray(validation) && validation.every((item) => typeof item === 'string')) {
      return validation.join('；');
    }
    const message = error.response?.data?.message;
    if (typeof message === 'string') return message;
    if (Array.isArray(message)) return message.join('；');
  }
  if (error instanceof Error) return error.message;
  return null;
}

function currency(value: number | undefined): string {
  return typeof value === 'number' ? value.toLocaleString('zh-TW') : '—';
}

const FIELD = 'mt-1 h-11 w-full border border-base-300 bg-base-100 p-2';
const NUMBER_FIELD = `${FIELD} text-right tabular-nums`;
const ACTION =
  'inline-flex min-h-11 items-center justify-center px-4 py-2 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY = `${ACTION} bg-primary text-primary-content hover:bg-primary/90`;
const OUTLINE = `${ACTION} border border-base-300 text-sm text-base-content hover:bg-base-300`;
const DANGER = `${ACTION} bg-error text-error-content hover:bg-error/90`;
const NOTICE = 'border border-warning p-2 text-sm text-base-content';
const CELL = 'px-2 py-2';
const NUM = 'px-2 py-2 text-right tabular-nums';
const HEAD = 'px-2 py-2 text-left';
const HEAD_NUM = 'px-2 py-2 text-right';

export default function TimedSettlementPage() {
  const { id } = useParams<{ id: string }>();
  const scope = useAuthStore((s) =>
    JSON.stringify([
      s.user?.id,
      s.sessionRevision,
      sessionVenueKey(s.accessToken),
      s.visiblePages,
      s.allowedActions,
    ]),
  );
  const canRead = useAuthStore(
    (s) => !!s.user?.isSystemUser || !!s.visiblePages?.includes('/tournament'),
  );
  if (!canRead) return <Navigate to="/" replace />;
  return <TimedSettlementContent key={`${id}:${scope}`} authScope={scope} />;
}
function TimedSettlementContent({ authScope }: { authScope: string }) {
  const canReport = useAuthStore(
    (s) => !!s.user?.isSystemUser || !!s.visiblePages?.includes('/accounting/report'),
  );
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation('tournament');
  const displayTimezone = useAccountingDisplayTimezone();
  const canUpdate = useAuthStore(
    (s) => !!s.user?.isSystemUser || !!s.allowedActions?.includes(ActionKey.TOURNAMENT_UPDATE),
  );
  const canCloseGame = useAuthStore(
    (s) => !!s.user?.isSystemUser || !!s.allowedActions?.includes(ActionKey.TOURNAMENT_COMPLETE),
  );
  const canReadExpenses = useAuthStore(
    (s) => !!s.user?.isSystemUser || !!s.allowedActions?.includes(ActionKey.ACCOUNTING_READ),
  );
  const canHandleCash = useAuthStore(
    (s) =>
      !!s.user?.isSystemUser ||
      [ActionKey.ACCOUNTING_READ, ActionKey.ACCOUNTING_EDIT].every((a) =>
        s.allowedActions?.includes(a),
      ),
  );
  const [custodyChecked, setCustodyChecked] = useState('');
  const describeError = (failure: unknown) => {
    if (isAxiosError(failure) && failure.response?.status === 403)
      return t('settlementFinal.errors.permission');
    const message = errorMessage(failure);
    const code = isAxiosError(failure)
      ? (failure.response?.data?.code ?? failure.response?.data?.errorCode ?? message)
      : message;
    if (typeof code === 'string' && t(`settlementFinal.errors.${code}`, { defaultValue: '' }))
      return t(`settlementFinal.errors.${code}`);
    if (message?.startsWith('Table count changed')) return t('settlementFinal.errors.counts');
    return message ?? t('settlementPage.genericError');
  };
  const queryClient = useQueryClient();
  const [form, setForm] = useState<DraftInput>(emptyDraft);
  const autoRefreshAttempt = useRef<string | null>(null);
  const piecesLockKey = `timed-chip-pieces:${authScope}:${id}`;
  const [piecesLocked, setPiecesLocked] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem(piecesLockKey) !== null;
    } catch {
      return true;
    }
  });
  const lockPieces = () => {
    setPiecesLocked(true);
    try {
      window.sessionStorage.setItem(piecesLockKey, 'locked');
    } catch {
      /* Keep in-memory lock. */
    }
  };
  const [dirty, setDirty] = useState(false);
  const [recoveryReload, setRecoveryReload] = useState(false);
  const [recoveryReloadFailed, setRecoveryReloadFailed] = useState(false);
  const correctionStep = useRef<HTMLFieldSetElement>(null);
  const [correctionReason, setCorrectionReason] = useState('');
  // CP acknowledges roster, fees and seats for one exact preview; any change clears it.
  const [cpChecked, setCpChecked] = useState('');
  const [receiptVersion, setReceiptVersion] = useState('');
  const [legacyChoices, setLegacyChoices] = useState<Record<string, 'C' | 'P'>>({});
  const [recoveryReason, setRecoveryReason] = useState('');
  const [cashRecoveryChecked, setCashRecoveryChecked] = useState(false);
  const finishIntent = useRef<{ source: string; key: string } | null>(null);
  const recoveryIntent = useRef<{ source: string; key: string; reason: string } | null>(null);
  const [selectedLevelId, setSelectedLevelId] = useState('');
  const [levelReason, setLevelReason] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [debouncedMemberSearch, setDebouncedMemberSearch] = useState('');
  const localBusiness = useLocalBusinessConsent();
  const latestRoster = useRef('');
  latestRoster.current = JSON.stringify(form.participants);
  const [memberIdentities, setMemberIdentities] = useState<
    Record<string, { memberId?: string; displayName: string; phoneSuffix: string | null }>
  >({});
  const [resetReason, setResetReason] = useState('');
  const [exceptionReason, setExceptionReason] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideDirty, setOverrideDirty] = useState(false);
  const [overrideRules, setOverrideRules] = useState<DraftInput['rules']>(emptyDraft.rules);
  const [initialCounts, setInitialCounts] = useState<Record<string, number>>({});
  const [raCounts, setRaCounts] = useState<Record<string, number>>({});
  const {
    data: loadedData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['timed-settlement', id, receiptVersion, authScope],
    queryFn: ({ signal }) =>
      api.get<SettlementResponse>(
        `/tournament/${id}/timed-settlement${receiptVersion ? `?version=${receiptVersion}` : ''}`,
        { signal },
      ),
    enabled: !!id,
    refetchInterval: 5000,
  });

  // A refused refresh must not leave protected cached data visible.
  const data = error || recoveryReload ? undefined : loadedData;
  useEffect(() => {
    if (data?.correctionPending && !receiptVersion) {
      correctionStep.current?.scrollIntoView?.({ block: 'start' });
      correctionStep.current?.focus();
    }
  }, [data?.correctionPending, receiptVersion]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedMemberSearch(memberSearch.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [memberSearch]);

  const {
    data: memberSearchData,
    isFetching: isSearchingMembers,
    isError: memberSearchFailed,
  } = useQuery<PaginatedMembersDto>({
    queryKey: ['timed-settlement-member-search', debouncedMemberSearch, authScope],
    queryFn: () => {
      const params = new URLSearchParams({
        page: '1',
        limit: '10',
        search: debouncedMemberSearch,
        status: 'ACTIVE',
      });
      return api.get<PaginatedMembersDto>(`/members?${params}`);
    },
    enabled: debouncedMemberSearch.length >= 1,
    staleTime: 2_000,
  });

  useEffect(() => {
    if (data?.identities) setMemberIdentities((current) => ({ ...current, ...data.identities }));
    if (data?.draft && !dirty) {
      setForm({
        ...(data.draft.settlementContractVersion === 2 ? { settlementContractVersion: 2 } : {}),
        paperReference: data.draft.paperReference,
        rules: data.draft.rules,
        participants: data.draft.participants,
      });
    } else if (data?.settlementLevel && !data.draft && !dirty) {
      setForm((current) => ({
        ...current,
        paperReference:
          current.paperReference ||
          `${new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())} ${data.tournamentName}`,
        rules: {
          ...current.rules,
          startingChips: data.settlementLevel!.startingChips,
          raChips: data.settlementLevel!.raChips,
          prizeContribution: data.settlementLevel!.prizeContribution,
          activityFee1: data.settlementLevel!.activityFee1,
          zCustomerLevelCode: defaultZCustomerLevelCode(
            current.rules.zCustomerLevelCode,
            data.settlementLevel!.zCustomerLevelCode,
            data.levels,
          ),
        },
      }));
    }
    if (!dirty && data?.settlementLevel) setSelectedLevelId(data.settlementLevel.id);
    if (data?.settlementLevel && !overrideDirty) {
      setInitialCounts(data.settlementLevel.initialCounts);
      setRaCounts(data.settlementLevel.raCounts);
      setOverrideRules(
        data.draft?.rules ?? {
          ...emptyDraft.rules,
          startingChips: data.settlementLevel.startingChips,
          raChips: data.settlementLevel.raChips,
          prizeContribution: data.settlementLevel.prizeContribution,
          activityFee1: data.settlementLevel.activityFee1,
          zCustomerLevelCode: defaultZCustomerLevelCode(
            '',
            data.settlementLevel.zCustomerLevelCode,
            data.levels,
          ),
        },
      );
    }
  }, [
    data?.draft,
    data?.settlementLevel,
    data?.identities,
    data?.tournamentName,
    data?.levels,
    dirty,
    overrideDirty,
  ]);

  const changeLevel = useMutation({
    mutationFn: () =>
      api.put(`/tournament/${id}/timed-settlement/level`, {
        levelId: selectedLevelId,
        reason: levelReason.trim(),
        expectedLevelVersion: data?.settlementLevelVersion ?? 0,
        expectedDraftVersion: data?.draft?.version ?? 0,
      }),
    onSuccess: async () => {
      setDirty(false);
      setLevelReason('');
      await queryClient.invalidateQueries({ queryKey: ['timed-settlement', id] });
      await queryClient.invalidateQueries({ queryKey: ['timed-table-counts', id] });
    },
  });

  const applyOverride = useMutation({
    mutationFn: () =>
      api.put(`/tournament/${id}/timed-settlement/override`, {
        rules: overrideRules,
        initialCounts,
        raCounts,
        reason: overrideReason.trim(),
        expectedLevelVersion: data?.settlementLevelVersion ?? 0,
        expectedDraftVersion: data?.draft?.version ?? 0,
      }),
    onSuccess: async () => {
      setOverrideDirty(false);
      setOverrideReason('');
      setDirty(false);
      await queryClient.invalidateQueries({ queryKey: ['timed-settlement', id] });
      await queryClient.invalidateQueries({ queryKey: ['timed-table-counts', id] });
    },
  });

  const reset = useMutation({
    mutationFn: () =>
      api.post(`/tournament/${id}/timed-settlement/reset`, {
        reason: resetReason.trim(),
        expectedVersion: data?.draft?.version,
      }),
    onSuccess: async () => {
      setDirty(false);
      setResetReason('');
      setExceptionReason('');
      await queryClient.invalidateQueries({ queryKey: ['timed-settlement', id] });
      await queryClient.invalidateQueries({ queryKey: ['timed-table-counts', id] });
    },
  });

  const acceptException = useMutation({
    mutationFn: () =>
      api.post(`/tournament/${id}/timed-settlement/denomination-exception`, {
        reason: exceptionReason.trim(),
        expectedVersion: data?.draft?.version,
      }),
    onSuccess: async () => {
      setExceptionReason('');
      await queryClient.invalidateQueries({ queryKey: ['timed-settlement', id] });
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      if (recoveryReload || !data || receiptVersion || data.phase === 'CLOSED')
        throw new Error(t('settlementFinal.recoveryReloadFailed'));
      const roster = JSON.stringify(form.participants);
      const ids = form.participants.map(
        (p) => memberIdentities[p.memberNo]?.memberId ?? data?.identities?.[p.memberNo]?.memberId,
      );
      if (ids.some((id) => !id)) throw Error(localBusiness.blockedMessage);
      const consent = await localBusiness.ask(
        ids as string[],
        'TIMED_DRAFT',
        undefined,
        () => latestRoster.current === roster,
      );
      if (!consent) throw Error(localBusiness.cancelledMessage);
      return api.put<SettlementResponse>(`/tournament/${id}/timed-settlement/draft`, {
        ...form,
        localInitializationConsent: consent,
        expectedVersion: data?.draft?.version ?? 0,
      });
    },
    onSuccess: async () => {
      setDirty(false);
      await queryClient.invalidateQueries({ queryKey: ['timed-settlement', id] });
    },
  });
  const refreshPreview = useMutation({
    mutationFn: (draft: DraftInput & { version: number }) =>
      api.put<SettlementResponse>(
        `/tournament/${id}/timed-settlement/draft`,
        savedDraftRefreshInput(draft),
      ),
    onSuccess: async (result) => {
      // A moved seat can remain stale until it is recounted. Do not refresh
      // the version created by this refresh again automatically.
      if (result.draft) autoRefreshAttempt.current = `${id}:${result.draft.version}`;
      await queryClient.invalidateQueries({ queryKey: ['timed-settlement', id] });
    },
  });
  useEffect(() => {
    if (!canUpdate || !data?.draft || save.isPending || save.error || refreshPreview.isPending)
      return;
    const attemptKey = autoRefreshKey({
      id,
      draftVersion: data.draft.version,
      previewStale: data.previewStale,
      dirty,
      overrideDirty,
      receiptVersion,
      confirmed: !!data?.confirmed,
      attemptedKey: autoRefreshAttempt.current,
    });
    if (!attemptKey) return;
    autoRefreshAttempt.current = attemptKey;
    refreshPreview.mutate(data.draft);
  }, [
    canUpdate,
    id,
    data?.draft,
    data?.previewStale,
    data?.confirmed,
    dirty,
    overrideDirty,
    receiptVersion,
    save.isPending,
    save.error,
    refreshPreview,
  ]);
  const workflow = useQuery({
    queryKey: ['timed-event-workflow', id, authScope],
    queryFn: () => api.get<EventWorkflow>(`/tournament/${id}/timed-settlement/workflow`),
    enabled: !!id && canCloseGame && !receiptVersion,
    refetchInterval: 5000,
  });
  const confirm = useMutation({
    onMutate: async () => {
      lockPieces();
      await queryClient.cancelQueries({ queryKey: ['timed-settlement', id] });
    },
    mutationFn: () =>
      api.post<EventPreparation>(
        `/tournament/${id}/timed-settlement/prepare`,
        {
          choices: legacyChoices,
          ...(correctionReason.trim() ? { reason: correctionReason.trim() } : {}),
        },
        { retryAfterAuthRefresh: false },
      ),
    onSuccess: async () => {
      lockPieces();
      setCpChecked('');
      await queryClient.invalidateQueries({ queryKey: ['timed-event-workflow', id] });
    },
  });
  const expenseSummary = useQuery({
    queryKey: ['timed-correction-expenses', id, authScope],
    queryFn: () => api.get<TimedEventExpenseSummary>(`/accounting/timed-events/${id}/expenses`),
    enabled: !!id && data?.correctionPending === true && canReadExpenses,
  });
  const draftFee = form.participants.reduce(
    (sum, p) => sum + (moneyCents(p.collectedVenueFee ?? '') ?? 0n),
    0n,
  );
  const paidExpenses = moneyCents(expenseSummary.data?.expenseTotal ?? '0') ?? 0n;
  const expenseDeficit =
    form.settlementContractVersion === 2 && paidExpenses > draftFee
      ? (Number(paidExpenses - draftFee) / 100).toFixed(2)
      : null;
  const canRecover = workflow.data?.canRecover === true;
  const candidatePreparation = workflow.error
    ? undefined
    : (workflow.data?.preparation ?? confirm.data);
  const prepared =
    candidatePreparation &&
    candidatePreparation.source.draftVersion === data?.draft?.version &&
    (!workflow.data || candidatePreparation.cycle === workflow.data.cycle) &&
    !dirty &&
    !overrideDirty &&
    !data?.previewStale
      ? candidatePreparation
      : null;
  const custodyLegs = [...(prepared?.source.custody?.legs ?? [])].sort((a, b) =>
    a.kind === b.kind ? 0 : a.kind === 'ENTRY' ? -1 : 1,
  );
  const finishEvent = useMutation({
    mutationFn: () => {
      if (!prepared) throw new Error(t('settlementFinal.stale'));
      if (prepared.source.custody && (!canHandleCash || custodyChecked !== prepared.sourceHash))
        throw new Error(t('settlementFinal.custodyAttestation'));
      if (finishIntent.current?.source !== prepared.sourceHash)
        finishIntent.current = { source: prepared.sourceHash, key: crypto.randomUUID() };
      return api.post<{ status: string; qualification?: SettlementQualificationStatus }>(
        `/tournament/${id}/timed-settlement/finish`,
        {
          operationKey: finishIntent.current.key,
          preparationId: prepared.id,
          sourceHash: prepared.sourceHash,
          cpAttested: true,
          ...(prepared.source.custody ? { custodyAttested: true } : {}),
        },
      );
    },
    onSuccess: async () => {
      setCpChecked('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['timed-settlement', id] }),
        queryClient.invalidateQueries({ queryKey: ['timed-event-workflow', id] }),
        queryClient.invalidateQueries({ queryKey: ['tournament', id] }),
      ]);
    },
  });
  const recoveryPreview = useMutation({
    mutationFn: () =>
      api.get<RecoveryPreview>(`/tournament/${id}/timed-settlement/recovery-preview`),
    onSuccess: () => setCashRecoveryChecked(false),
  });
  const recoverEvent = useMutation({
    mutationFn: () => {
      const source = recoveryPreview.data;
      if (!source) throw new Error(t('settlementFinal.recoveryPreview'));
      if (recoveryIntent.current?.source !== source.sourceHash)
        recoveryIntent.current = {
          source: source.sourceHash,
          key: crypto.randomUUID(),
          reason: recoveryReason.trim(),
        };
      return api.post(`/tournament/${id}/timed-settlement/recover`, {
        operationKey: recoveryIntent.current.key,
        expectedCycle: source.cycle,
        sourceHash: source.sourceHash,
        reason: recoveryIntent.current.reason,
        cashAttested: true,
      });
    },
    onSuccess: async () => {
      // Only this acknowledged recovery starts a provably new cycle. Its draft has no preview.
      try {
        window.sessionStorage.removeItem(piecesLockKey);
      } catch {
        /* Fail closed. */
      }
      setPiecesLocked(false);
      setRecoveryReload(true);
      setRecoveryReloadFailed(false);
      setDirty(false);
      setOverrideDirty(false);
      setLegacyChoices({});
      setReceiptVersion('');
      setCustodyChecked('');
      save.reset();
      refreshPreview.reset();
      confirm.reset();
      recoveryPreview.reset();
      setCpChecked('');
      setRecoveryReason('');
      setCashRecoveryChecked(false);
      recoveryIntent.current = null;
      try {
        await Promise.all([
          queryClient.refetchQueries(
            { queryKey: ['timed-settlement', id] },
            { throwOnError: true },
          ),
          queryClient.refetchQueries(
            { queryKey: ['timed-event-workflow', id] },
            { throwOnError: true },
          ),
          queryClient.invalidateQueries({ queryKey: ['tournament', id] }),
        ]);
        setRecoveryReload(false);
      } catch {
        setRecoveryReloadFailed(true);
      }
    },
  });
  const qualificationRetry = useMutation({
    mutationFn: () =>
      api.post<SettlementQualificationStatus>(
        `/tournament/${id}/timed-settlement/qualification-retry`,
        { expectedVersion: data?.confirmed?.version },
      ),
  });
  const qualificationStatus =
    qualificationRetry.data ?? finishEvent.data?.qualification ?? data?.qualification;
  const isClosed = data?.phase === 'CLOSED';
  const changeRule = (key: keyof DraftInput['rules'], value: number) => {
    setForm((current) => ({ ...current, rules: { ...current.rules, [key]: value } }));
    setDirty(true);
  };
  const changeParticipant = (index: number, change: Partial<ParticipantInput>) => {
    setForm((current) => ({
      ...current,
      participants: current.participants.map((item, position) =>
        position === index ? { ...item, ...change } : item,
      ),
    }));
    setDirty(true);
  };
  const addParticipant = (member: MemberDto) => {
    if (form.participants.some((participant) => participant.memberNo === member.memberNo)) return;
    setForm((current) => ({
      ...current,
      participants: [
        ...current.participants,
        { ...emptyParticipant(current.settlementContractVersion !== 2), memberNo: member.memberNo },
      ],
    }));
    setMemberIdentities((current) => ({
      ...current,
      [member.memberNo]: {
        memberId: member.id,
        displayName: member.nickname?.trim() || member.realName?.trim() || member.memberNo,
        phoneSuffix: member.phone?.replace(/\D/g, '').slice(-6) || null,
      },
    }));
    setMemberSearch('');
    setDebouncedMemberSearch('');
    setDirty(true);
  };
  const preview = data?.preview;
  const confirmed = data?.confirmed;
  const receipt = receiptVersion
    ? confirmed
    : prepared
      ? {
          payload: prepared.source.payload,
          preview: prepared.source.preview,
          version: prepared.cycle,
          confirmedAt: prepared.createdAt,
        }
      : confirmed;
  const memberSearchReady =
    debouncedMemberSearch.length > 0 && debouncedMemberSearch === memberSearch.trim();
  const canConfirm =
    canCloseGame &&
    !!data?.draft &&
    data.phase === 'COUNTING' &&
    !!preview &&
    preview.totals.chipDifference === 0 &&
    preview.totals.activityFee2 >= 0 &&
    (preview.totals.missingCounts ?? 0) === 0 &&
    (!preview.totals.denominationMismatch || !!preview.denominationException?.reason) &&
    !data?.previewStale &&
    !dirty &&
    !receiptVersion &&
    !save.isPending &&
    !save.error &&
    !refreshPreview.isPending &&
    !confirm.isPending;
  const cpKey = prepared?.sourceHash ?? '';
  const cpReady = !!cpKey && !dirty && !overrideDirty && cpChecked === cpKey;
  useEffect(() => {
    if (dirty || overrideDirty) setCpChecked('');
  }, [dirty, overrideDirty]);

  const guide = settlementStep({
    hasDraft: !!data?.draft,
    dirty: dirty || overrideDirty,
    confirmed: !!prepared,
    hasPreview: !!preview,
    previewStale: !!data?.previewStale,
    missingCounts: preview?.totals.missingCounts ?? 0,
    chipDifference: preview?.totals.chipDifference ?? 0,
    denominationBlocked: !!preview?.totals.denominationMismatch && !preview?.denominationException,
    counting: data?.phase === 'COUNTING',
    saveFailed: !!save.error,
    awardsExceedPool: (preview?.totals.activityFee2 ?? 0) < 0,
  });
  // One primary action per view: the current step's own button, else the guide's link.
  const savePrimary = guide.step === 1;
  const confirmPrimary = guide.step === 3 && !guide.blocker;
  const printPrimary = false;
  const finalPrimary = guide.step === 4;
  const stepHeading = (n: 1 | 2 | 3 | 4) => (
    <h2 className="flex items-center gap-2 text-lg font-semibold">
      <span className="inline-flex h-7 w-7 items-center justify-center bg-primary text-sm text-primary-content tabular-nums">
        {n}
      </span>
      {t(`settlementFlow.step${n}`)}
    </h2>
  );
  // The single status line of step 3; it names the first thing that blocks confirmation.
  const verdict = (() => {
    if (!preview) return null;
    const totals = preview.totals;
    if (data?.previewStale)
      return {
        ok: false,
        text: refreshPreview.isPending
          ? t('settlementPage.staleRefreshing')
          : dirty || overrideDirty
            ? t('settlementPage.staleSaveFirst')
            : t('settlementPage.staleNotUpdated'),
      };
    if (dirty || save.isPending || save.error)
      return { ok: false, text: t('settlementPage.verdict.needsUpdate') };
    if ((totals.missingCounts ?? 0) > 0)
      return {
        ok: false,
        text: t('settlementPage.verdict.missingCounts', { count: totals.missingCounts }),
      };
    if (totals.chipDifference !== 0)
      return {
        ok: false,
        text: t('settlementPage.verdict.chipMismatch', {
          difference: currency(totals.chipDifference),
        }),
      };
    if (totals.activityFee2 < 0)
      return { ok: false, text: t('settlementPage.verdict.awardsExceedPool') };
    if (totals.denominationMismatch)
      return {
        ok: false,
        text: preview.denominationException
          ? t('settlementPage.verdict.piecesRecorded')
          : t('settlementPage.verdict.piecesNeedReason'),
      };
    return {
      ok: true,
      text: totals.expectedPieces
        ? t('settlementPage.verdict.donePieces')
        : t('settlementPage.verdict.doneTotal'),
    };
  })();
  const piecesNeedReason =
    !!preview?.totals.denominationMismatch &&
    preview.totals.chipDifference === 0 &&
    (preview.totals.missingCounts ?? 0) === 0 &&
    !preview.denominationException;

  if (error) {
    return (
      <AdminPage header={<AdminPageHeader title={t('settlementPage.title')} />}>
        <p role="alert" className="text-error">
          {recoveryReload ? t('settlementFinal.recoveryReloadFailed') : describeError(error)}
        </p>
      </AdminPage>
    );
  }

  return (
    <AdminPage className="admin-page-body space-y-6">
      {localBusiness.confirmation}
      <AdminPageHeader
        className="print:hidden"
        title={t('settlementPage.title')}
        description={t('settlementPage.intro')}
        back={
          <Link
            to={`/tournament/${id}`}
            className="inline-flex min-h-11 items-center text-sm text-base-content underline underline-offset-4 hover:text-primary"
          >
            {t('settlementPage.back')}
          </Link>
        }
      />

      {isLoading && (
        <p role="status" className="text-base-content/70">
          {t('settlementPage.loading')}
        </p>
      )}
      {data && !isClosed && (
        <SettlementSteps
          step={guide.step}
          blocker={guide.blocker}
          emphasis={!savePrimary && !confirmPrimary && !finalPrimary}
        />
      )}
      {error && (
        <p role="alert" className="text-error">
          {describeError(error)}
        </p>
      )}

      {recoveryReload && (
        <p role="alert">
          {t(
            recoveryReloadFailed
              ? 'settlementFinal.recoveryReloadFailed'
              : 'settlementFinal.recoveryReloading',
          )}
        </p>
      )}
      {isClosed && (
        <p
          role={data?.closure?.sourceValid ? 'status' : 'alert'}
          className="border border-base-300 p-4"
        >
          {t(data?.closure?.sourceValid ? 'gameClose.closed' : 'gameClose.unverified')}
        </p>
      )}
      {/* Once confirmed, steps 1-3 stay one click away for a correction, so the sign
          sheet and payout follow the guide directly. */}
      <CorrectionFold
        folded={!!confirmed && !isClosed && !data?.correctionPending}
        summary={t('settlementPage.correctionSummary', { version: confirmed?.version })}
      >
        <fieldset
          disabled={!canUpdate || !data || recoveryReload || !!receiptVersion || isClosed}
          ref={correctionStep}
          tabIndex={-1}
          id="settlement-data"
          hidden={isClosed}
          className="scroll-mt-4 space-y-4 border border-base-300 p-4 print:hidden"
        >
          {stepHeading(1)}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="block text-sm">
                {t('settlementPage.level')}
                <select
                  className={FIELD}
                  value={selectedLevelId}
                  onChange={(event) => setSelectedLevelId(event.target.value)}
                >
                  <option value="">{t('settlementPage.noLevel')}</option>
                  {data?.settlementLevel &&
                    !data.settlementLevels.some(
                      (level) => level.id === data.settlementLevel?.id,
                    ) && (
                      <option value={data.settlementLevel.id}>
                        {data.settlementLevel.name} · {t('settlementPage.snapshot')}
                      </option>
                    )}
                  {data?.settlementLevels?.map((level) => (
                    <option key={level.id} value={level.id}>
                      {t('settlementPage.levelOption', {
                        name: level.name,
                        starting: level.startingChips.toLocaleString('zh-TW'),
                        ra: level.raChips.toLocaleString('zh-TW'),
                      })}
                    </option>
                  ))}
                </select>
              </label>
              {selectedLevelId && selectedLevelId !== data?.settlementLevel?.id && (
                <>
                  <p className="text-sm">
                    {t('settlementPage.levelChangeNotice', {
                      name: data?.settlementLevels.find((level) => level.id === selectedLevelId)
                        ?.name,
                    })}
                  </p>
                  <label className="block text-sm">
                    {t('settlementPage.levelReason')}
                    <input
                      className={FIELD}
                      value={levelReason}
                      onChange={(event) => setLevelReason(event.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className={OUTLINE}
                    disabled={Boolean(
                      !canUpdate ||
                      data?.confirmed ||
                      dirty ||
                      !levelReason.trim() ||
                      changeLevel.isPending ||
                      receiptVersion,
                    )}
                    onClick={() => changeLevel.mutate()}
                  >
                    {t('settlementPage.applyLevel')}
                  </button>
                  {changeLevel.error && (
                    <p role="alert" className="text-error text-sm">
                      {describeError(changeLevel.error)}
                    </p>
                  )}
                </>
              )}
            </div>
            <label className="block text-sm">
              {t('settlementPage.paperReference')}
              <input
                className={FIELD}
                value={form.paperReference}
                onChange={(event) => {
                  setForm({ ...form, paperReference: event.target.value });
                  setDirty(true);
                }}
              />
            </label>
          </div>
          <div className="space-y-2">
            <label htmlFor="timed-settlement-member-search" className="block text-sm font-medium">
              {t('settlementPage.addParticipant')}
            </label>
            <input
              id="timed-settlement-member-search"
              type="search"
              autoComplete="off"
              aria-controls="timed-settlement-member-results"
              aria-expanded={memberSearch.trim().length > 0}
              placeholder={t('settlementPage.searchPlaceholder')}
              className="h-11 w-full border border-base-300 bg-base-100 p-2 sm:max-w-md"
              value={memberSearch}
              onChange={(event) => setMemberSearch(event.target.value)}
            />
            {memberSearch.trim().length > 0 && (
              <div
                id="timed-settlement-member-results"
                className="relative max-h-64 overflow-y-auto border border-base-300 sm:max-w-2xl"
              >
                <p className="sr-only" role="status" aria-live="polite">
                  {!memberSearchReady || isSearchingMembers
                    ? t('settlementPage.searchingStatus')
                    : memberSearchFailed
                      ? t('settlementPage.searchFailedStatus')
                      : t('settlementPage.searchFound', {
                          count: memberSearchData?.data.length ?? 0,
                        })}
                </p>
                {(!memberSearchReady || isSearchingMembers) && (
                  <p className="p-3 text-sm text-base-content/70">
                    {t('settlementPage.searching')}
                  </p>
                )}
                {memberSearchReady && memberSearchFailed && (
                  <p className="p-3 text-sm text-error">{t('settlementPage.searchFailed')}</p>
                )}
                {memberSearchReady &&
                  !isSearchingMembers &&
                  !memberSearchFailed &&
                  (memberSearchData?.data.length ?? 0) === 0 && (
                    <p className="p-3 text-sm text-base-content/70">
                      {t('settlementPage.searchEmpty')}
                    </p>
                  )}
                {memberSearchReady &&
                  !isSearchingMembers &&
                  !memberSearchFailed &&
                  memberSearchData?.data.map((member) => {
                    const alreadyAdded = form.participants.some(
                      (participant) => participant.memberNo === member.memberNo,
                    );
                    return (
                      <button
                        key={member.id}
                        type="button"
                        className="flex min-h-11 w-full items-center justify-between gap-3 border-b border-base-300 px-3 py-2 text-left last:border-b-0 hover:bg-base-200 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={alreadyAdded}
                        onClick={() => addParticipant(member)}
                      >
                        <span>
                          <span className="font-medium">
                            {member.nickname?.trim() || member.realName?.trim() || member.memberNo}
                          </span>
                          <span className="ml-2 text-xs text-base-content/70">
                            #{member.memberNo}
                          </span>
                          {member.phone && (
                            <span className="ml-2 text-xs text-base-content/70">
                              {t('settlementPage.phoneSuffix', {
                                suffix: member.phone.replace(/\D/g, '').slice(-6),
                              })}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-xs">
                          {alreadyAdded
                            ? t('settlementPage.added')
                            : t('settlementPage.addToEvent')}
                        </span>
                      </button>
                    );
                  })}
              </div>
            )}
          </div>
          <p className="text-sm text-base-content/70">
            {t(
              form.settlementContractVersion === 2
                ? 'settlementPage.collectedVenueFeeHint'
                : 'settlementPage.legacyMethod',
            )}
          </p>
          <div role="group" className="space-y-2" aria-label={t('settlementPage.participants')}>
            {form.participants.length === 0 && (
              <p className="border border-base-300 p-4 text-sm text-base-content/70">
                {t('settlementPage.noParticipants')}
              </p>
            )}
            {form.participants.map((participant, index) => {
              const identity =
                memberIdentities[participant.memberNo] ?? data?.identities?.[participant.memberNo];
              return (
                <div
                  key={participant.memberNo}
                  className="flex flex-wrap items-center gap-x-4 gap-y-2 border border-base-300 px-3 py-2"
                >
                  <div className="min-w-0 flex-1 basis-48">
                    <p className="font-medium">{identity?.displayName ?? participant.memberNo}</p>
                    <p className="text-xs text-base-content/70">
                      {participant.memberNo} ·{' '}
                      {t('settlementPage.phoneSuffixLine', {
                        suffix: identity?.phoneSuffix ?? t('settlementPage.notProvided'),
                      })}
                    </p>
                  </div>
                  <label className="text-sm">
                    {t('settlementPage.raCount')}
                    <input
                      aria-label={t('settlementPage.raCountAria', { n: index + 1 })}
                      type="number"
                      inputMode="numeric"
                      min="0"
                      step="1"
                      className="ml-2 h-11 w-20 border border-base-300 bg-base-100 p-2 text-right tabular-nums"
                      value={participant.raCount}
                      onChange={(event) =>
                        changeParticipant(index, { raCount: Number(event.target.value) })
                      }
                    />
                  </label>
                  {form.settlementContractVersion === 2 && (
                    <>
                      <label className="text-sm">
                        {t('settlementPage.collectedVenueFee')}
                        <input
                          aria-label={t('settlementPage.collectedVenueFeeAria', { n: index + 1 })}
                          type="number"
                          inputMode="decimal"
                          min="0"
                          max="9999999999.99"
                          step="0.01"
                          required
                          className="ml-2 h-11 w-28 border border-base-300 bg-base-100 p-2 text-right tabular-nums"
                          value={participant.collectedVenueFee ?? ''}
                          onChange={(event) =>
                            changeParticipant(index, { collectedVenueFee: event.target.value })
                          }
                        />
                      </label>
                      <fieldset
                        className="flex flex-wrap items-center gap-3"
                        aria-label={t('settlementPage.methodAria', { n: index + 1 })}
                      >
                        <legend className="text-sm">{t('settlementPage.method')}</legend>
                        {(['CASH', 'ZIPA'] as const).map((method) => (
                          <label key={method} className="flex min-h-11 items-center gap-2 text-sm">
                            <input
                              type="radio"
                              name={`method-${participant.memberNo}`}
                              value={method}
                              checked={participant.settlementMethod === method}
                              onChange={() =>
                                changeParticipant(index, { settlementMethod: method })
                              }
                            />
                            {t(
                              method === 'CASH'
                                ? 'settlementPage.methodCash'
                                : 'settlementPage.methodZipa',
                            )}
                          </label>
                        ))}
                      </fieldset>
                    </>
                  )}
                  <label className="flex min-h-11 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-5 w-5"
                      aria-label={t('settlementPage.activityAria', { n: index + 1 })}
                      checked={participant.activityParticipant}
                      onChange={(event) =>
                        changeParticipant(index, { activityParticipant: event.target.checked })
                      }
                    />
                    {t('settlementPage.activity')}
                  </label>
                  <button
                    type="button"
                    className="min-h-11 px-3 text-error hover:bg-error/10"
                    onClick={() => {
                      setForm((current) => ({
                        ...current,
                        participants: current.participants.filter(
                          (item) => item.memberNo !== participant.memberNo,
                        ),
                      }));
                      setDirty(true);
                    }}
                  >
                    {t('settlementPage.remove')}
                  </button>
                </div>
              );
            })}
          </div>
          {expenseDeficit && (
            <p role="alert" className="text-error">
              {t('eventExpenses.draftDeficit', { amount: expenseDeficit })}
            </p>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              className={savePrimary ? PRIMARY : OUTLINE}
              disabled={
                !canUpdate ||
                recoveryReload ||
                !!expenseDeficit ||
                (!dirty && !data?.draft) ||
                save.isPending ||
                refreshPreview.isPending ||
                overrideDirty ||
                !form.rules.zCustomerLevelCode ||
                (form.settlementContractVersion === 2 &&
                  form.participants.some(
                    (p) => !/^\d{1,10}(\.\d{1,2})?$/.test(p.collectedVenueFee ?? ''),
                  ))
              }
              onClick={() => save.mutate()}
            >
              {t('settlementPage.saveAndPreview')}
            </button>
          </div>
          {!form.rules.zCustomerLevelCode && (
            <p role="status" className={NOTICE}>
              {t('settlementPage.needZCustomerLevel')}
            </p>
          )}
          {save.error && (
            <p role="alert" className="text-error">
              {describeError(save.error)}
            </p>
          )}
          {dirty && (
            <p role="status" className={NOTICE}>
              {t('settlementPage.unsaved')}
            </p>
          )}
          <details className="border border-base-300 p-3">
            <summary className="flex min-h-11 cursor-pointer items-center font-medium">
              {t('settlementPage.advancedSummary', {
                starting: form.rules.startingChips.toLocaleString('zh-TW'),
                ra: form.rules.raChips.toLocaleString('zh-TW'),
              })}
            </summary>
            <p className="mt-2 text-sm text-base-content/70">{t('settlementPage.advancedHint')}</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {(
                [
                  ['startingChips', 'settlementPage.rules.startingChips'],
                  ['raChips', 'settlementPage.rules.raChips'],
                  ['prizeContribution', 'settlementPage.rules.prizeContributionPerUnit'],
                  ['activityFee1', 'settlementPage.rules.activityFee1PerUnit'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className="text-sm">
                  {t(label)}
                  <input
                    type="number"
                    min="0"
                    step="1"
                    readOnly={Boolean(data?.settlementLevel)}
                    className={NUMBER_FIELD}
                    value={form.rules[key]}
                    onChange={(event) => changeRule(key, Number(event.target.value))}
                  />
                </label>
              ))}
              <label className="text-sm">
                {t('settlementPage.zCustomerLevel')}
                <select
                  className={FIELD}
                  value={form.rules.zCustomerLevelCode}
                  disabled={Boolean(data?.draft)}
                  onChange={(event) => {
                    setForm((current) => ({
                      ...current,
                      rules: { ...current.rules, zCustomerLevelCode: event.target.value },
                    }));
                    setDirty(true);
                  }}
                >
                  <option value="">{t('settlementPage.chooseVenueLevel')}</option>
                  {data?.levels
                    .filter((level) => level.isZCustomer)
                    .map((level) => (
                      <option key={level.code} value={level.code}>
                        {level.displayName} ({level.code})
                      </option>
                    ))}
                </select>
              </label>
            </div>
            <p className="mt-2 text-sm text-base-content/70 tabular-nums">
              {t('settlementPage.perUnitFormula', {
                contribution: currency(form.rules.prizeContribution),
                fee: currency(form.rules.activityFee1),
                net: currency(form.rules.prizeContribution - form.rules.activityFee1),
              })}
            </p>
            {data?.settlementLevel && !data.confirmed && (
              <div className="mt-4 space-y-3 border border-base-300 p-3">
                <h3 className="font-medium">{t('settlementPage.overrideTitle')}</h3>
                <p className="text-sm text-base-content/70">{t('settlementPage.overrideHint')}</p>
                <div className="grid grid-cols-2 gap-2">
                  {(
                    [
                      ['startingChips', 'settlementPage.rules.startingChips'],
                      ['raChips', 'settlementPage.rules.raChips'],
                      ['prizeContribution', 'settlementPage.rules.prizeContribution'],
                      ['activityFee1', 'settlementPage.rules.activityFee1'],
                    ] as const
                  ).map(([key, label]) => (
                    <label key={key} className="text-sm">
                      {t(label)}
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className={NUMBER_FIELD}
                        value={overrideRules[key]}
                        onChange={(event) => {
                          setOverrideRules((current) => ({
                            ...current,
                            [key]: Number(event.target.value),
                          }));
                          setOverrideDirty(true);
                        }}
                      />
                    </label>
                  ))}
                </div>
                <p className="text-sm text-base-content/70 tabular-nums">
                  {t('settlementPage.perUnitFormula', {
                    contribution: currency(overrideRules.prizeContribution),
                    fee: currency(overrideRules.activityFee1),
                    net: currency(overrideRules.prizeContribution - overrideRules.activityFee1),
                  })}
                </p>
                <label className="block text-sm">
                  {t('settlementPage.zCustomerLevel')}
                  <select
                    className={FIELD}
                    value={overrideRules.zCustomerLevelCode}
                    onChange={(event) => {
                      setOverrideRules((current) => ({
                        ...current,
                        zCustomerLevelCode: event.target.value,
                      }));
                      setOverrideDirty(true);
                    }}
                  >
                    <option value="">{t('settlementPage.chooseVenueLevel')}</option>
                    {data.levels
                      .filter((level) => level.isZCustomer)
                      .map((level) => (
                        <option key={level.code} value={level.code}>
                          {level.displayName}
                        </option>
                      ))}
                  </select>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {(
                    [
                      ['initial', initialCounts, setInitialCounts],
                      ['ra', raCounts, setRaCounts],
                    ] as const
                  ).map(([kind, values, setter]) => (
                    <fieldset key={kind} className="space-y-2 border border-base-300 p-2">
                      <legend className="text-sm font-medium">
                        {kind === 'initial'
                          ? t('settlementPage.initialPieces')
                          : t('settlementPage.raPieces')}
                      </legend>
                      {data.settlementLevel!.denominations.map((face) => (
                        <label key={face} className="block text-sm tabular-nums">
                          {face.toLocaleString('zh-TW')}
                          <input
                            type="number"
                            min="0"
                            step="1"
                            className={NUMBER_FIELD}
                            value={values[String(face)] ?? 0}
                            onChange={(event) => {
                              setter((current) => ({
                                ...current,
                                [face]: Number(event.target.value),
                              }));
                              setOverrideDirty(true);
                            }}
                          />
                        </label>
                      ))}
                    </fieldset>
                  ))}
                </div>
                <label className="block text-sm">
                  {t('settlementPage.overrideReason')}
                  <input
                    className={FIELD}
                    value={overrideReason}
                    onChange={(event) => setOverrideReason(event.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className={OUTLINE}
                  disabled={
                    !overrideDirty || !overrideReason.trim() || dirty || applyOverride.isPending
                  }
                  onClick={() => applyOverride.mutate()}
                >
                  {t('settlementPage.applyOverride')}
                </button>
                {applyOverride.error && (
                  <p role="alert" className="text-error text-sm">
                    {describeError(applyOverride.error)}
                  </p>
                )}
              </div>
            )}
          </details>
          {data?.draft && !data.confirmed && (
            <details className="border border-error p-3">
              <summary className="flex min-h-11 cursor-pointer items-center text-error">
                {t('settlementPage.resetSummary')}
              </summary>
              <p className="mt-2 text-sm">{t('settlementPage.resetHint')}</p>
              <label className="mt-2 block text-sm">
                {t('settlementPage.resetReason')}
                <input
                  className={FIELD}
                  value={resetReason}
                  onChange={(event) => setResetReason(event.target.value)}
                />
              </label>
              <button
                type="button"
                className={`mt-2 ${DANGER}`}
                disabled={!resetReason.trim() || dirty || reset.isPending}
                onClick={() => {
                  if (window.confirm(t('settlementPage.resetConfirm'))) reset.mutate();
                }}
              >
                {t('settlementPage.reset')}
              </button>
              {reset.error && (
                <p role="alert" className="text-error text-sm">
                  {describeError(reset.error)}
                </p>
              )}
            </details>
          )}
        </fieldset>

        <section
          id="settlement-counting"
          hidden={isClosed}
          className="scroll-mt-4 space-y-3 border border-base-300 p-4 print:hidden"
        >
          {stepHeading(2)}
          {dirty || !data?.draft ? (
            <p className="text-sm">{t('settlementPage.countingBefore')}</p>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/tables" className={OUTLINE}>
                {t('settlementPage.goToTables')}
              </Link>
              <p className="text-sm">{t('settlementPage.countingHint')}</p>
            </div>
          )}
          {preview && (
            <p role="status" className="text-sm tabular-nums">
              {t('settlementPage.countedProgress', {
                counted: preview.totals.participants - (preview.totals.missingCounts ?? 0),
                total: preview.totals.participants,
              })}
            </p>
          )}
        </section>

        {data?.draft && !preview && !isClosed && (
          <section
            id="settlement-preview"
            className="space-y-4 border border-base-300 p-4 print:hidden"
          >
            {stepHeading(3)}
            <p
              role="status"
              className="border border-error bg-error/15 p-4 text-lg font-bold text-error"
            >
              {data.previewStale
                ? t(
                    refreshPreview.isPending
                      ? 'settlementPage.staleRefreshing'
                      : 'settlementPage.staleNotUpdated',
                  )
                : t('settlementPage.verdict.countsRequired')}
            </p>
            {data.previewStale && (
              <button
                type="button"
                className={OUTLINE}
                disabled={
                  !canUpdate ||
                  refreshPreview.isPending ||
                  dirty ||
                  overrideDirty ||
                  !!receiptVersion
                }
                onClick={() => canUpdate && refreshPreview.mutate(data.draft!)}
              >
                {t('settlementPage.retryPreview')}
              </button>
            )}
            {refreshPreview.error && <p role="alert">{describeError(refreshPreview.error)}</p>}
            <button type="button" className={OUTLINE} disabled>
              {t('settlementFinal.prepare')}
            </button>
          </section>
        )}

        {preview && (
          <section
            id="settlement-preview"
            hidden={isClosed}
            className="scroll-mt-4 space-y-4 border border-base-300 p-4 print:hidden"
          >
            {stepHeading(3)}
            {verdict && (
              <div
                className={`space-y-2 border p-4 ${verdict.ok ? 'border-success bg-success/15 text-success' : 'border-error bg-error/15 text-error'}`}
              >
                <p role="status" className="text-lg font-bold">
                  {verdict.text}
                </p>
                {data?.previewStale &&
                  !dirty &&
                  !overrideDirty &&
                  !save.isPending &&
                  !refreshPreview.isPending && (
                    <button
                      type="button"
                      className={OUTLINE}
                      disabled={!canUpdate}
                      onClick={() => canUpdate && data.draft && refreshPreview.mutate(data.draft)}
                    >
                      {t('settlementPage.retryPreview')}
                    </button>
                  )}
                {refreshPreview.error && (
                  <p role="alert" className="text-sm">
                    {describeError(refreshPreview.error)}
                  </p>
                )}
                {preview.totals.denominationMismatch &&
                  preview.totals.chipDifference === 0 &&
                  (preview.totals.missingCounts ?? 0) === 0 &&
                  (preview.denominationException ? (
                    <p className="text-sm text-base-content">
                      {t('settlementPage.exceptionReason', {
                        reason: preview.denominationException.reason,
                      })}
                    </p>
                  ) : (
                    <div className="flex flex-wrap items-end gap-3 text-base-content">
                      <label className="block min-w-0 flex-1 basis-60 text-sm">
                        {t('settlementPage.exceptionReasonInput')}
                        <input
                          className={FIELD}
                          value={exceptionReason}
                          disabled={!canUpdate}
                          onChange={(event) => setExceptionReason(event.target.value)}
                        />
                      </label>
                      <button
                        type="button"
                        className={OUTLINE}
                        disabled={
                          !canUpdate ||
                          !exceptionReason.trim() ||
                          dirty ||
                          data?.previewStale ||
                          acceptException.isPending
                        }
                        onClick={() => acceptException.mutate()}
                      >
                        {t('settlementPage.recordException')}
                      </button>
                      {acceptException.error && (
                        <p role="alert" className="w-full text-error">
                          {describeError(acceptException.error)}
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            )}
            <SettlementSummary
              totals={preview.totals}
              keys={[
                'participants',
                'raCount',
                'units',
                'expectedChips',
                'countedChips',
                'chipDifference',
              ]}
            />
            <MemberChipTable
              participants={preview.participants}
              raCounts={raCountsByMember(data?.draft?.participants)}
            />
            {data?.chipPiecesReviewAvailable === true &&
              data.preview &&
              data.phase === 'COUNTING' &&
              !receiptVersion &&
              !dirty &&
              !overrideDirty &&
              !data.previewStale &&
              !piecesLocked &&
              !confirm.isPending &&
              !workflow.data?.preparation &&
              !workflow.data?.finish && (
                <MemberChipPiecesReview
                  participants={data.preview.participants}
                  expectedPieces={data.preview.totals.expectedPieces}
                  countedPieces={data.preview.totals.countedPieces}
                />
              )}
            <details className="border border-base-300 p-3" open={piecesNeedReason}>
              <summary className="flex min-h-11 cursor-pointer items-center font-medium">
                {t('settlementPage.detailsSummary')}
              </summary>
              <div className="mt-3 space-y-4">
                <p className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                  <span>{t('settlementPage.metaTournament', { name: data?.tournamentName })}</span>
                  <span>
                    {t('settlementPage.metaLevel', {
                      name: data?.settlementLevel?.name ?? t('settlementPage.notSelected'),
                    })}
                  </span>
                  <span>
                    {t('settlementPage.metaPaper', {
                      reference: data?.draft?.paperReference ?? t('settlementPage.notSaved'),
                    })}
                  </span>
                </p>
                <TimedSettlementBudgetSummary
                  totals={preview.totals}
                  calculationRevision={preview.calculationRevision}
                />
                {preview.totals.expectedPieces && preview.totals.countedPieces && (
                  <div className="overflow-x-auto border border-base-300">
                    <table className="w-full text-sm">
                      <caption className="px-2 py-2 text-left font-medium">
                        {t('settlementPage.pieces.caption')}
                      </caption>
                      <thead>
                        <tr className="border-b border-base-300">
                          <th scope="col" className={HEAD_NUM}>
                            {t('settlementPage.pieces.face')}
                          </th>
                          <th scope="col" className={HEAD_NUM}>
                            {t('settlementPage.pieces.expected')}
                          </th>
                          <th scope="col" className={HEAD_NUM}>
                            {t('settlementPage.pieces.counted')}
                          </th>
                          <th scope="col" className={HEAD_NUM}>
                            {t('settlementPage.pieces.difference')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(preview.totals.expectedPieces).map(([face, expected]) => {
                          const actual = preview.totals.countedPieces?.[face] ?? 0;
                          return (
                            <tr
                              key={face}
                              className={`border-b border-base-300 ${actual !== expected ? 'bg-error/15 text-error' : ''}`}
                            >
                              <td className={NUM}>{Number(face).toLocaleString('zh-TW')}</td>
                              <td className={NUM}>{expected}</td>
                              <td className={NUM}>{actual}</td>
                              <td className={NUM}>{actual - expected}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                <ResultTable participants={preview.participants} />
              </div>
            </details>
            {confirmed && (
              <label className="block text-sm">
                {t('settlementPage.correctionReason')}
                <input
                  className={FIELD}
                  value={correctionReason}
                  disabled={!canCloseGame}
                  onChange={(event) => setCorrectionReason(event.target.value)}
                />
              </label>
            )}
            {form.settlementContractVersion !== 2 && (
              <div className="space-y-2">
                <p>{t('settlementFinal.legacyChoices')}</p>
                {preview.participants.map(
                  (p) =>
                    p.memberId && (
                      <label key={p.memberId} className="flex min-h-11 items-center gap-3">
                        {p.displayName ?? p.nickname ?? p.memberNo}
                        <select
                          className={FIELD}
                          aria-label={`${t('settlementFinal.method')} ${p.memberNo}`}
                          value={legacyChoices[p.memberId] ?? ''}
                          onChange={(e) =>
                            setLegacyChoices((c) => ({
                              ...c,
                              [p.memberId!]: e.target.value as 'C' | 'P',
                            }))
                          }
                        >
                          <option value="">{t('settlementFinal.chooseMethod')}</option>
                          <option value="C">{t('settlementFinal.cash')}</option>
                          <option value="P">ZIPA</option>
                        </select>
                      </label>
                    ),
                )}
              </div>
            )}
            <button
              type="button"
              className={confirmPrimary ? PRIMARY : OUTLINE}
              disabled={!canConfirm}
              onClick={() => confirm.mutate()}
            >
              {t('settlementFinal.prepare')}
            </button>
            {confirm.error && (
              <p role="alert" className="text-error">
                {describeError(confirm.error)}
              </p>
            )}
          </section>
        )}
      </CorrectionFold>

      {(data?.draft || confirmed || receiptVersion) && (
        <div id="settlement-receipt" className="scroll-mt-4 space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-3 print:hidden">
            <div className="space-y-1">
              {stepHeading(3)}
              {!receipt && <p className="text-sm">{t('settlementFlow.receiptPending')}</p>}
              {confirmed && canReport && (
                <p className="text-sm">
                  {t('settlementPage.cpCheck.dayIncome')}{' '}
                  <Link className="link" to="/accounting/report">
                    {t('settlementPage.cpCheck.openReport')}
                  </Link>
                </p>
              )}
            </div>
            <label className="block text-sm">
              {t('settlementPage.reprintVersion')}
              <input
                type="number"
                min="1"
                step="1"
                className="mt-1 ml-2 h-11 w-24 border border-base-300 bg-base-100 p-2 text-right tabular-nums"
                value={receiptVersion}
                onChange={(event) => setReceiptVersion(event.target.value)}
              />
            </label>
          </div>
          {receipt && (
            <section
              className="settlement-receipt space-y-4 border border-base-300 p-4"
              aria-label={t('settlementPage.receiptLabel')}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold">限時賽結算簽收表</h2>
                  <div className="text-sm print:hidden">
                    <p>
                      紙本編號：{receipt.payload.paperReference}　版本：{receipt.version}
                      {prepared && !receiptVersion ? '　產生時間：' : '　確認時間：'}
                      {formatAccountingInstant(
                        receipt.confirmedAt,
                        displayTimezone ?? '',
                        i18n.language,
                      ) ?? '—'}
                    </p>
                    <p>
                      賽事：{receipt.preview.tournamentName ?? '歷史賽事'}　應有籌碼：
                      {currency(receipt.preview.totals.expectedChips)}
                    </p>
                  </div>
                  <dl className="settlement-receipt-metadata hidden text-sm tabular-nums print:flex">
                    <div className="settlement-receipt-reference">
                      <dt>紙本編號：</dt>
                      <dd>
                        {receipt.payload.paperReference} / v{receipt.version}
                        {prepared && !receiptVersion && (
                          <>
                            {' '}
                            / {prepared.cycle}-{prepared.sourceHash.slice(0, 12)}
                          </>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>時間：</dt>
                      <dd>
                        {formatAccountingInstant(
                          receipt.confirmedAt,
                          displayTimezone ?? '',
                          i18n.language,
                        ) ?? '—'}
                      </dd>
                    </div>
                    <div>
                      <dt>人次：</dt>
                      <dd>{currency(receipt.preview.totals.participants)}</dd>
                    </div>
                    <div>
                      <dt>總買入：</dt>
                      <dd>{currency(receipt.preview.totals.units)}</dd>
                    </div>
                    <div>
                      <dt>總碼量：</dt>
                      <dd>{currency(receipt.preview.totals.expectedChips)}</dd>
                    </div>
                  </dl>
                </div>
                <div className="flex flex-wrap gap-2 print:hidden">
                  <button
                    type="button"
                    className={printPrimary ? PRIMARY : OUTLINE}
                    onClick={() => window.print()}
                  >
                    {t('settlementPage.print')}
                  </button>
                  {prepared && !receiptVersion && (
                    <a href="#settlement-finalize" className={OUTLINE}>
                      {t('settlementFlow.action4')}
                    </a>
                  )}
                </div>
              </div>
              {prepared && !receiptVersion && (
                <p className="font-mono text-sm print:hidden">
                  {t('settlementFinal.sheetCode')}: {prepared.cycle}-
                  {prepared.sourceHash.slice(0, 12)}
                </p>
              )}
              <div className="print:hidden">
                <TimedSettlementBudgetSummary
                  totals={receipt.preview.totals}
                  calculationRevision={receipt.preview.calculationRevision}
                />
              </div>
              <dl className="settlement-receipt-chip-check text-sm tabular-nums print:hidden">
                <div>
                  <dt>應有籌碼：</dt>
                  <dd>{currency(receipt.preview.totals.expectedChips)}</dd>
                </div>
                <div>
                  <dt>實點籌碼：</dt>
                  <dd>{currency(receipt.preview.totals.countedChips)}</dd>
                </div>
                <div>
                  <dt>籌碼差額：</dt>
                  <dd>{currency(receipt.preview.totals.chipDifference)}</dd>
                </div>
              </dl>
              {receipt.preview.totals.expectedPieces && receipt.preview.totals.countedPieces && (
                <p className="text-sm tabular-nums print:hidden">
                  {t('settlementPage.piecesCheck')}
                  {Object.entries(receipt.preview.totals.expectedPieces)
                    .map(
                      ([face, expected]) =>
                        `${Number(face).toLocaleString()} ${receipt.preview.totals.countedPieces?.[face] ?? 0}/${expected}`,
                    )
                    .join(' · ')}
                </p>
              )}
              {receipt.preview.denominationException && (
                <p className="border border-base-300 p-2 text-sm print:hidden">
                  {t('settlementPage.piecesExceptionReason', {
                    reason: receipt.preview.denominationException.reason,
                  })}
                </p>
              )}
              {prepared?.source.custody && (
                <div className="space-y-1">
                  <p>{t('settlementFinal.custodyDescription')}</p>
                  {custodyLegs.map((l) => (
                    <p key={`${l.memberId}:${l.kind}`}>
                      {t(
                        l.kind === 'ENTRY'
                          ? 'settlementFinal.custodyEntry'
                          : 'settlementFinal.custodyAward',
                      )}
                      : {currency(Number(l.amount))}
                    </p>
                  ))}
                  <p>
                    {t('settlementFinal.custodyRemainder')}:{' '}
                    {currency(Number(prepared.source.custody.feeRemainder))}
                  </p>
                </div>
              )}
              <ResultTable participants={receipt.preview.participants} signed />
              <p className="text-xs print:hidden">
                本表為簽收紀錄；列印不會新增獎金支付或點數入帳。
              </p>
            </section>
          )}
        </div>
      )}

      {!receiptVersion && data && (
        <section
          id="settlement-finalize"
          className="scroll-mt-4 space-y-4 border border-base-300 p-4 print:hidden"
        >
          {stepHeading(4)}
          {workflow.data?.finish ? (
            <p role="status">{t('settlementFinal.completed')}</p>
          ) : (
            <>
              <p>{t('settlementFinal.description')}</p>
              {isClosed && !prepared && (
                <button
                  type="button"
                  className={OUTLINE}
                  disabled={!canCloseGame || confirm.isPending}
                  onClick={() => confirm.mutate()}
                >
                  {t('settlementFinal.prepareRemaining')}
                </button>
              )}
              {prepared ? (
                <>
                  <p className="font-mono">
                    {t('settlementFinal.sheetCode')}: {prepared.cycle}-
                    {prepared.sourceHash.slice(0, 12)}
                  </p>
                  <details className="border border-base-300 p-3">
                    <summary className="min-h-11 cursor-pointer font-semibold">
                      {t('settlementFinal.reviewAmounts')}
                    </summary>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[480px] text-sm">
                        <caption className="sr-only">{t('settlementFinal.reviewAmounts')}</caption>
                        <thead>
                          <tr>
                            <th scope="col" className="text-center">
                              {t('settlementFinal.member')}
                            </th>
                            <th scope="col" className="text-center">
                              {t('settlementFinal.method')}
                            </th>
                            <th scope="col" className="text-center">
                              {t('settlementFinal.entryDue')}
                            </th>
                            <th scope="col" className="text-center">
                              {t('settlementFinal.netAward')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {prepared.source.members.map((m) => (
                            <tr key={m.memberId}>
                              <td className="text-center">
                                {memberIdentities[m.memberNo]?.displayName ?? m.memberNo}
                              </td>
                              <td className="text-center">
                                {m.method === 'C' ? t('settlementFinal.cash') : 'ZIPA'}
                              </td>
                              <td className="text-center tabular-nums">
                                {currency(Number(m.unpaid))}
                              </td>
                              <td className="text-center tabular-nums">
                                {currency(Number(m.award))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-2 text-sm">{t('settlementFinal.recordedNotice')}</p>
                  </details>
                  {prepared.source.custody && (
                    <div className="space-y-2 border border-base-300 p-3">
                      <p>{t('settlementFinal.custodyDescription')}</p>
                      <ul className="tabular-nums">
                        {custodyLegs.map((l) => (
                          <li key={`${l.memberId}:${l.kind}`}>
                            {t(
                              l.kind === 'ENTRY'
                                ? 'settlementFinal.custodyEntry'
                                : 'settlementFinal.custodyAward',
                            )}
                            : {currency(Number(l.amount))}
                          </li>
                        ))}
                      </ul>
                      <p>
                        {t('settlementFinal.custodyRemainder')}:{' '}
                        {currency(Number(prepared.source.custody.feeRemainder))}
                      </p>
                      {!canHandleCash && (
                        <p role="alert">{t('settlementFinal.custodyAuthority')}</p>
                      )}
                      <label className="flex min-h-11 items-center gap-2">
                        <input
                          type="checkbox"
                          checked={custodyChecked === prepared.sourceHash}
                          disabled={!canHandleCash || finishEvent.isPending}
                          onChange={(e) =>
                            setCustodyChecked(e.target.checked ? prepared.sourceHash : '')
                          }
                        />
                        {t('settlementFinal.custodyAttestation')}
                      </label>
                    </div>
                  )}
                  <CpCheckPanel
                    summary={cpCheckSummary(prepared.source.preview.participants)}
                    raCount={prepared.source.preview.totals.raCount}
                    totals={prepared.source.preview.totals}
                    checked={cpReady}
                    disabled={finishEvent.isPending || !canCloseGame}
                    onChange={(checked) => setCpChecked(checked ? cpKey : '')}
                  />
                  <p>{t('settlementFinal.attestation')}</p>
                  <button
                    type="button"
                    className={PRIMARY}
                    disabled={
                      !cpReady ||
                      !canCloseGame ||
                      finishEvent.isPending ||
                      (!!prepared.source.custody &&
                        (!canHandleCash || custodyChecked !== prepared.sourceHash))
                    }
                    onClick={() => {
                      if (window.confirm(t('settlementFinal.finishPrompt'))) finishEvent.mutate();
                    }}
                  >
                    {t('settlementFinal.finish')}
                  </button>
                </>
              ) : (
                <p>{t('settlementFinal.prepareFirst')}</p>
              )}
            </>
          )}
          {(workflow.error || finishEvent.error) && (
            <p role="alert" className="text-error">
              {describeError(workflow.error ?? finishEvent.error)}
            </p>
          )}
          <TimedEventExpenses tournamentId={id!} finished={!!workflow.data?.finish} />
          {data.confirmed && (
            <div className="space-y-2">
              <p role="status">
                {t(
                  qualificationStatus
                    ? `timedFinancial.qualification${qualificationStatus.status}`
                    : 'timedFinancial.qualificationUnavailable',
                )}
              </p>
              <button
                type="button"
                className={OUTLINE}
                disabled={!canCloseGame || qualificationRetry.isPending}
                onClick={() => qualificationRetry.mutate()}
              >
                {t('settlementFinal.retryQualification')}
              </button>
              {qualificationRetry.error && (
                <p role="alert" className="text-error">
                  {describeError(qualificationRetry.error)}
                </p>
              )}
            </div>
          )}
          {canRecover && (
            <details className="border border-base-300 p-3 space-y-3">
              <summary className="min-h-11 cursor-pointer">{t('settlementFinal.recovery')}</summary>
              <p>{t('settlementFinal.recoveryLimits')}</p>
              <button
                type="button"
                className={OUTLINE}
                disabled={recoveryPreview.isPending || recoverEvent.isPending}
                onClick={() => recoveryPreview.mutate()}
              >
                {t('settlementFinal.recoveryPreview')}
              </button>
              {recoveryPreview.data && (
                <>
                  <p>
                    {recoveryPreview.data.businessDate} ·{' '}
                    {t('settlementFinal.cycle', { cycle: recoveryPreview.data.cycle })}
                  </p>
                  <ul>
                    {recoveryPreview.data.members.map((m) => (
                      <li key={m.id} className="py-1 tabular-nums">
                        {m.displayName || m.memberNo} · {t('settlementFinal.awardReturn')}:{' '}
                        {m.awardReturn} · {t('settlementFinal.entryRefund')}: {m.entryRefund}
                      </li>
                    ))}
                  </ul>
                  {!!recoveryPreview.data.custody?.length && (
                    <div className="space-y-2">
                      <p>{t('settlementFinal.custodyReverse')}</p>
                      <ul className="tabular-nums">
                        {[...recoveryPreview.data.custody]
                          .sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'AWARD' ? -1 : 1))
                          .map((l, i) => (
                            <li key={i}>
                              {t(
                                l.kind === 'AWARD'
                                  ? 'settlementFinal.custodyAwardReturn'
                                  : 'settlementFinal.custodyEntryRefund',
                              )}
                              : {currency(Number(l.amount))}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                  <label className="block">
                    {t('settlementFinal.reason')}
                    <input
                      className={FIELD}
                      maxLength={500}
                      disabled={recoverEvent.isPending || !!recoverEvent.error}
                      value={recoveryReason}
                      onChange={(e) => setRecoveryReason(e.target.value)}
                    />
                  </label>
                  <label className="flex min-h-11 items-center gap-2">
                    <input
                      type="checkbox"
                      checked={cashRecoveryChecked}
                      onChange={(e) => setCashRecoveryChecked(e.target.checked)}
                    />
                    {t('settlementFinal.recoveryAttestation')}
                  </label>
                  <button
                    type="button"
                    className={OUTLINE}
                    disabled={
                      !recoveryReason.trim() || !cashRecoveryChecked || recoverEvent.isPending
                    }
                    onClick={() => {
                      if (window.confirm(t('settlementFinal.recoveryPrompt')))
                        recoverEvent.mutate();
                    }}
                  >
                    {t('settlementFinal.recover')}
                  </button>
                </>
              )}
              {(recoveryPreview.error || recoverEvent.error) && (
                <p role="alert" className="text-error">
                  {describeError(recoveryPreview.error ?? recoverEvent.error)} ·{' '}
                  {t('settlementFinal.support')}
                </p>
              )}
            </details>
          )}
        </section>
      )}
    </AdminPage>
  );
}

/** Folds steps 1-3 into one closed section once a version is confirmed; otherwise renders them as is. */
function CorrectionFold({
  folded,
  summary,
  children,
}: {
  folded: boolean;
  summary: string;
  children: ReactNode;
}) {
  if (!folded) return <>{children}</>;
  return (
    <details className="border border-base-300 p-3 print:hidden">
      <summary className="flex min-h-11 cursor-pointer items-center font-medium">{summary}</summary>
      <div className="mt-3 space-y-6">{children}</div>
    </details>
  );
}

function raCountsByMember(participants: ParticipantInput[] | undefined): Record<string, number> {
  return Object.fromEntries((participants ?? []).map((p) => [p.memberNo, p.raCount]));
}

function rankLabel(p: ParticipantResult, t: TFunction<'tournament'>, signed = false) {
  if (p.rankEligibility === 'ELIMINATED')
    return signed ? '淘汰' : t('settlementPage.result.eliminated');
  if (p.rankEligibility === 'PENDING_COUNT')
    return signed ? '待點算' : t('settlementPage.result.pendingCount');
  return p.rank;
}

function seatLabel(p: ParticipantResult, t: TFunction<'tournament'>): string {
  if (p.tableNo != null)
    return t('settlementPage.result.seat', { table: p.tableNo, seat: p.seatNo ?? '—' });
  if (p.seat?.includes(':')) return t('settlementPage.result.legacySeat');
  return p.seat ?? t('settlementPage.result.unassigned');
}

/** What staff check first: who played, their R/A, counted chips and what each receives. */
function MemberChipTable({
  participants,
  raCounts,
}: {
  participants: ParticipantResult[];
  raCounts: Record<string, number>;
}) {
  const { t } = useTranslation('tournament');
  return (
    <div
      role="region"
      aria-label={t('settlementPage.result.compactCaption')}
      tabIndex={0}
      className="overflow-x-auto"
    >
      <table className="w-full text-sm">
        <caption className="sr-only">{t('settlementPage.result.compactCaption')}</caption>
        <thead>
          <tr className="border-b border-base-300">
            <th scope="col" className={`${HEAD_NUM} whitespace-nowrap`}>
              {t('settlementPage.result.rank')}
            </th>
            <th scope="col" className={`${HEAD} whitespace-nowrap`}>
              {t('settlementPage.result.tableSeat')}
            </th>
            <th scope="col" className={`${HEAD} whitespace-nowrap`}>
              {t('settlementPage.result.player')}
            </th>
            <th scope="col" className={`${HEAD_NUM} whitespace-nowrap`}>
              {t('settlementPage.result.ra')}
            </th>
            <th scope="col" className={`${HEAD_NUM} whitespace-nowrap`}>
              {t('settlementPage.result.counted')}
            </th>
            <th scope="col" className={`${HEAD_NUM} whitespace-nowrap`}>
              {t('settlementPage.result.prizePay')}
            </th>
            <th scope="col" className={`${HEAD_NUM} whitespace-nowrap`}>
              {t('settlementPage.result.z')}
            </th>
          </tr>
        </thead>
        <tbody>
          {participants.map((p, index) => (
            <tr key={`${p.memberNo}-${index}`} className="border-b border-base-300">
              <td className={`${NUM} whitespace-nowrap`}>{rankLabel(p, t)}</td>
              <td className={`${CELL} whitespace-nowrap`}>{seatLabel(p, t)}</td>
              <td className={`${CELL} whitespace-nowrap`}>
                {p.displayName ?? p.nickname ?? p.memberNo}
                <span className="ml-2 text-xs text-base-content/70">{p.memberNo}</span>
              </td>
              <td className={`${NUM} whitespace-nowrap`}>{currency(raCounts[p.memberNo])}</td>
              <td className={`${NUM} whitespace-nowrap`}>{currency(p.finalChips)}</td>
              <td className={`${NUM} whitespace-nowrap`}>{currency(p.prize ?? undefined)}</td>
              <td className={`${NUM} whitespace-nowrap`}>{currency(p.zPoints)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultTable({
  participants,
  signed = false,
}: {
  participants: ParticipantResult[];
  signed?: boolean;
}) {
  const { t } = useTranslation('tournament');
  // The signed table is the printed sign sheet; its wording stays zh-TW.
  const label = (key: string, signedText: string) =>
    signed ? signedText : t(`settlementPage.result.${key}`);
  return (
    <div
      {...(signed
        ? {}
        : { role: 'region', 'aria-label': t('settlementPage.result.caption'), tabIndex: 0 })}
      className={signed ? '' : 'overflow-x-auto'}
    >
      <table className={`relative w-full text-sm ${signed ? '' : 'min-w-[900px]'}`}>
        <caption className="sr-only">{label('caption', '簽收明細')}</caption>
        <thead>
          <tr className="border-b border-base-300 print:border-base-content">
            <th scope="col" className={HEAD_NUM}>
              {label('rank', '名次')}
            </th>
            {!signed && (
              <th scope="col" className={HEAD}>
                {t('settlementPage.result.tableSeat')}
              </th>
            )}
            <th scope="col" className={HEAD}>
              {label('member', '姓名／等級')}
            </th>
            <th scope="col" className={HEAD_NUM}>
              {label('chips', '買入／結算／淨籌碼')}
            </th>
            <th scope="col" className={HEAD_NUM}>
              {label('awards', '原始獎金／名次獎金／總獎金')}
            </th>
            <th scope="col" className={HEAD_NUM}>
              {label('points', '活動／參賽點／名次點')}
            </th>
            <th scope="col" className={HEAD_NUM}>
              {label('prize', '實領獎金／Z 點')}
            </th>
            <th scope="col" className={HEAD}>
              {label('note', '備註')}
            </th>
            {signed && (
              <th scope="col" className={HEAD}>
                簽收
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {participants.map((p, index) => (
            <tr key={`${p.memberNo}-${index}`} className="border-b border-base-300 align-top">
              <td className={`${NUM} whitespace-nowrap`}>{rankLabel(p, t, signed)}</td>
              {!signed && <td className={CELL}>{seatLabel(p, t)}</td>}
              <td className={CELL}>
                {!signed && (
                  <>
                    {p.memberNo}
                    <br />
                  </>
                )}
                {p.displayName ?? p.nickname ?? '—'}
                <br />
                {p.memberLevel?.displayName ?? '—'}
                <br className={signed ? 'print:hidden' : ''} />
                <span className={`text-xs ${signed ? 'print:hidden' : ''}`}>
                  {p.settlementMethod
                    ? t(
                        p.settlementMethod === 'CASH'
                          ? 'settlementPage.methodCash'
                          : 'settlementPage.methodZipa',
                      )
                    : t('settlementPage.legacyMethodShort')}
                  {p.collectedVenueFee !== undefined && (
                    <>
                      {' '}
                      · {t('settlementPage.collectedVenueFee')}: {p.collectedVenueFee}
                    </>
                  )}
                </span>
              </td>
              <td className={NUM}>
                {currency(p.purchasedChips)}
                <br />
                {currency(p.finalChips)}
                <br />
                {currency(p.netChips)}
              </td>
              <td className={NUM}>
                {currency(p.baseAward)}
                <br />
                {currency(p.rankBonus)}
                <br />
                <strong>{currency(p.totalAward)}</strong>
              </td>
              <td className={NUM}>
                {p.activityParticipant === false ? label('no', '否') : label('yes', '是')}
                <br />
                {currency(p.participationPoints ?? undefined)}
                <br />
                {currency(p.rankPoints ?? undefined)}
              </td>
              <td className={NUM}>
                {signed ? (
                  <strong className="underline">{currency(p.prize ?? undefined)}</strong>
                ) : (
                  currency(p.prize ?? undefined)
                )}
                <br />
                {!signed && (
                  <>
                    {currency(p.zipaCredits ?? undefined)}
                    <br />
                  </>
                )}
                {currency(p.zPoints ?? undefined)}
              </td>
              <td className={CELL}>{p.note ?? ''}</td>
              {signed && <td className={`${CELL} h-12`} />}
            </tr>
          ))}
        </tbody>
      </table>
      <p className={`mt-1 text-sm text-base-content/70 ${signed ? 'print:hidden' : ''}`}>
        {t('settlementPage.result.footnote')}
      </p>
    </div>
  );
}
